import { isEmpty } from 'lodash'
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState
} from 'react'

export const useMarkItems = <T extends { [key: string]: any }>(
  setIds?: Dispatch<SetStateAction<string[] | undefined>>,
  identityField = 'id',
  // Comparing objects with JSON.stringify or directly by links is bad, so I added this flag to slowly migrate to primitive values
  shouldCompareByIdentityField = false
): UseMarkItemsReturnType<T> => {
  const [items, setItems] = useState<T[]>([])
  const [all, setAll] = useState<boolean>(false)

  const addItem = useCallback((item: T | T[]) => {
    setItems((prev) => {
      if (Array.isArray(item)) {
        return [...prev, ...item]
      }
      return [...prev, item]
    })
  }, [])

  const removeItem = useCallback((item: T | T[]) => {
    if (Array.isArray(item)) {
      if (shouldCompareByIdentityField) {
        setItems((prev) =>
          prev.filter(
            (i) => !item.find((it) => it[identityField] === i[identityField])
          )
        )
        return
      }

      const itemsTobeDeleted = new Set(item)

      setItems((prev) => {
        return prev.filter((item) => !itemsTobeDeleted.has(item))
      })
      return
    }

    if (shouldCompareByIdentityField) {
      setItems((prev) =>
        prev.filter((i) => item[identityField] !== i[identityField])
      )
      return
    }

    setItems((prev) => prev.filter((el) => el !== item))
  }, [])

  const toggleAll = useCallback((items?: T[]) => {
    setAll((prev) => !prev)
    if (items) {
      setItems((prev) => {
        if (prev.length !== 0) {
          if (shouldCompareByIdentityField) {
            if (
              items.every((val) =>
                prev.find((i) => i[identityField] === val[identityField])
              )
            ) {
              return []
            }
            const newItems = [...prev]
            items.forEach((val) => {
              newItems.push(val)
            })
            return newItems
          }

          if (
            items.every((val) =>
              prev.find((i) => JSON.stringify(i) === JSON.stringify(val))
            )
          ) {
            return []
          }
          items.forEach((val) => prev.push(val))
          return Array.from(prev)
        }
        return items
      })
    }
  }, [])

  useEffect(() => {
    if (setIds) {
      setIds(items.map((item) => item[identityField]))
    }
  }, [items.length])

  const emptyItems = useCallback(() => {
    setItems([])
  }, [])

  const hasItem = useCallback(
    (item: T, itemsArr = items) => {
      if (shouldCompareByIdentityField) {
        return !isEmpty(
          itemsArr.find((i) => i[identityField] === item[identityField])
        )
      }

      return !isEmpty(
        itemsArr.find((i) => JSON.stringify(i) === JSON.stringify(item))
      )
    },
    [items]
  )

  const toggleItem = useCallback(
    (item: T) => {
      setItems((prev) => {
        const hasItemAlready = hasItem(item, prev)
        if (hasItemAlready) {
          if (shouldCompareByIdentityField) {
            return prev.filter((i) => i[identityField] !== item[identityField])
          }

          return prev.filter((i) => JSON.stringify(i) !== JSON.stringify(item))
        } else {
          return [...prev, item]
        }
      })
    },
    [hasItem]
  )

  return {
    removeItem,
    addItem,
    emptyItems,
    hasItem,
    items,
    toggleItem,
    toggleAll,
    all
  }
}

export type UseMarkItemsReturnType<T> = {
  removeItem: (item: T | T[]) => void
  addItem: (item: T | T[]) => void
  emptyItems: () => void
  hasItem: (item: T, itemsArr?: T[]) => boolean
  items: T[]
  all: boolean
  toggleItem: (item: T) => void
  toggleAll: (items?: T[]) => void
}
